import { isClient } from '@/utils/isClient';
import { ProductConfigWithSku } from '@sales-domain/product-config';
import { QueryParams } from '@sales-domain/types';

const USER_BROWSER_ID_KEY = 'UserBrowserId';
const SESSION_QUERY_PARAMETERS_KEY = 'QueryParameters';

type QueryParamsType = {
  couponCode?: string;
  billId?: string;
  promoCode?: string;
} & Record<string, any>;

type UtmParams = {
  utm_source?: string;
  utm_campaign?: string;
  utm_medium?: string;
  utm_term?: string;
  utm_content?: string;
};
type SalesAttributionQueryParamsType = UtmParams & {
  salesPersonId?: string;
  salesSessionId?: string;
  storeCode?: string;
  productSku?: string;
  dealerCode?: string;
  referralPartner?: string;
  vendorData?: string | Record<string, any>;
  sourceApp?: string;
  salesChannel?: string;
};

export default class UserBrowser {
  protected static instance: UserBrowser | undefined;

  static Partner?: string;
  static Stage?: string;
  static AppVersion?: string;
  static AppName?: string;

  UserBrowserId: string;
  sessionQueryParameters:
    | (QueryParamsType & SalesAttributionQueryParamsType)
    | undefined;
  ProductConfig?: ProductConfigWithSku;
  ProductSku?: string;
  ProductOfferId: string | undefined;

  protected constructor() {
    this.UserBrowserId = this.retrieveId();
    this.initQueryParameters();
  }
  public updateBrowserSession(productConfig?: ProductConfigWithSku) {
    this.initQueryParameters();

    this.ProductConfig = productConfig;
    this.ProductSku = productConfig?.productSku;
  }
  public retrieveId(): string {
    if (!isClient()) {
      return crypto.randomUUID();
    }

    try {
      let id = window?.sessionStorage.getItem(USER_BROWSER_ID_KEY);
      if (!id) {
        id = crypto.randomUUID();
      }
      window?.sessionStorage.setItem(USER_BROWSER_ID_KEY, id);
      return id;
    } catch (e) {
      return crypto.randomUUID();
    }
  }

  public initQueryParameters() {
    if (!isClient()) {
      return;
    }

    let sessionQueryParameters;
    try {
      sessionQueryParameters = window?.sessionStorage.getItem(
        SESSION_QUERY_PARAMETERS_KEY,
      );
    } catch (e) {}
    if (
      !sessionQueryParameters ||
      (window?.location.search.length &&
        window?.location.search !== sessionQueryParameters)
    ) {
      this.sessionQueryParameters = this.parseQueryParams(
        new URLSearchParams(window?.location.search),
      );
      if (this.sessionQueryParameters?.poid) {
        this.ProductOfferId = this.sessionQueryParameters.poid;
      }
      try {
        window?.sessionStorage.setItem(
          SESSION_QUERY_PARAMETERS_KEY,
          JSON.stringify(this.sessionQueryParameters),
        );
      } catch (e) {}
    }
  }

  public static get(): UserBrowser {
    if (!UserBrowser.instance) {
      UserBrowser.instance = new UserBrowser();
    }

    return UserBrowser.instance;
  }

  protected static clearInstance() {
    UserBrowser.instance = undefined;
  }

  protected parseQueryParams(
    params: URLSearchParams = new URLSearchParams(''),
  ): QueryParamsType {
    const paramsAsObject = Object.fromEntries(params);
    const knownKeys = Object.keys(QueryParams);
    const unknownKeys = Object.keys(paramsAsObject || {}).filter(
      (k) => !knownKeys.includes(k),
    );

    const unknownParams: Record<string, string> = {};
    unknownKeys.forEach(
      (key: string) => (unknownParams[key] = paramsAsObject[key]),
    );
    const vendorData = {
      v_data: paramsAsObject[QueryParams.VendorData],
      ...paramsAsObject,
    };
    try {
      vendorData.v_data =
        vendorData.v_data != null ? { ...JSON.parse(vendorData.v_data) } : {};
    } catch (error: any) {
      // do nothing, its not a json string
    }

    return (
      paramsAsObject && {
        salesPersonId: paramsAsObject[QueryParams.AgentId] || undefined,
        salesSessionId: paramsAsObject[QueryParams.SalesSessionId] || undefined,
        storeCode: paramsAsObject[QueryParams.StoreCode] || undefined,
        productSku: paramsAsObject[QueryParams.ProductSku] || this.ProductSku,
        dealerCode: paramsAsObject[QueryParams.DealerCode] || undefined,
        dealerName: paramsAsObject[QueryParams.DealerName] || undefined,
        referralPartner: paramsAsObject[QueryParams.ReferrerCode] || 'Asurion',
        vendorData,
        sourceApp: paramsAsObject[QueryParams.SourceApp] || 'd2c-sales-tool',
        salesChannel:
          paramsAsObject[QueryParams.SalesChannel] ||
          'Digital_registration_web',
        ...(Object.keys(paramsAsObject)
          .filter((paramKey: string) => paramKey.toLowerCase().includes('utm_'))
          .reduce(
            (acc: Record<string, any>, paramKey: string) => ({
              ...acc,
              [paramKey]: paramsAsObject[paramKey],
            }),
            {},
          ) as UtmParams),
        promoCode: paramsAsObject[QueryParams.PromoCode] || undefined,
        ...unknownParams,
      }
    );
  }

  public getAttributionParams(): Record<string, any> {
    if (!this.sessionQueryParameters) {
      return {
        referralPartner: 'Asurion',
        salesChannel: 'Digital_registration_web',
      };
    }
    return {
      salesPersonId: this.sessionQueryParameters.salesPersonId,
      salesSessionId: this.sessionQueryParameters.salesSessionId,
      storeCode: this.sessionQueryParameters.storeCode,
      dealerCode: this.sessionQueryParameters.dealerCode,
      dealerName: this.sessionQueryParameters.dealerName,
      promoCode: this.sessionQueryParameters.promoCode,
      referrerCode: this.sessionQueryParameters.referrerCode,
      referralPartner: this.sessionQueryParameters.referralPartner || 'Asurion',
      vendorData: this.sessionQueryParameters.vendorData,
      salesChannel:
        this.sessionQueryParameters[QueryParams.SalesChannel] ||
        'Digital_registration_web',
      utmParams: {
        utmSource: this.sessionQueryParameters.utm_source,
        utmCampaign: this.sessionQueryParameters.utm_campaign,
        utmMedium: this.sessionQueryParameters.utm_medium,
        utmContent: this.sessionQueryParameters.utm_content,
      },
    };
  }
}

export { QueryParams };
